import type { KeyedMutator } from 'swr';
import type { PropsWithChildren } from 'react';
import type { IApiResponse } from 'src/types/api';
import type { IRestaurantData } from 'src/types/restaurant';

import { useNavigate } from 'react-router';
import { useMemo, useState, useEffect, useCallback, createContext } from 'react';

import { paths } from 'src/routes/paths';

import { useGetRestaurants } from 'src/actions/restaurant';

type DashboardContextType = {
  selectedRestaurant: IRestaurantData | null;
  setSelectedRestaurant: (restaurant: IRestaurantData) => void;
  restaurants: IRestaurantData[];
  mutate: KeyedMutator<IApiResponse<IRestaurantData[]>>;
};

export const DashboardContext = createContext<DashboardContextType>({} as DashboardContextType);

export const DashboardContextProvider = (props: PropsWithChildren) => {
  const { children } = props;
  const [selectedRestaurant, setSelectedRestaurant] = useState<IRestaurantData | null>(null);

  const { restaurants, mutate, loading } = useGetRestaurants();

  const navigate = useNavigate();

  const handleSelect = useCallback(
    (restaurant: IRestaurantData) => {
      setSelectedRestaurant({ ...restaurant });
    },
    [setSelectedRestaurant]
  );

  const memoizedValue = useMemo(
    () => ({
      selectedRestaurant: selectedRestaurant || restaurants?.[0] || null,
      setSelectedRestaurant: handleSelect,
      restaurants: restaurants || [],
      mutate,
    }),
    [restaurants, selectedRestaurant, handleSelect, mutate]
  );

  useEffect(() => {
    const { pathname } = window.location;
    const isEditOrCreatePage =
      pathname.includes('edit') || pathname.includes('create') || pathname.includes('new');

    if (isEditOrCreatePage) {
      navigate(-1);
    }

    // navigate shouldn't be in the dependency array because it will trigger the useEffect on every render
    // so it will navigate back on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant?._id]);

  useEffect(() => {
    if (loading) return;

    if (restaurants.length === 0) {
      navigate(paths.dashboard.root);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurants]);

  return <DashboardContext.Provider value={memoizedValue}>{children}</DashboardContext.Provider>;
};
